import React from "react";
import PolicyLayout from "../../../components/PolicyLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function SmartSpyPrivacy() {
  return (
    <PolicyLayout
      title="privacy policy"
      appName="Smart Spy Detector"
      date="DECEMBER 2025"
    >
      <div className={s.title}>1) Introduction and Scope</div>
      <p>
        This Privacy Policy explains how “Smart Spy Detector” (the “App”)
        collects, uses, stores, and discloses information. The App helps you
        scan your surroundings for potential hidden cameras or electronic bugs
        using your device sensors (magnetometer) and camera-based optical
        reflections. The App is designed to work offline without account
        registration or a custom backend; core data remains on your device
        unless you explicitly share exports. This Policy covers onboarding,
        sensor access, camera/flashlight use, haptics and sound alerts, local
        history, subscriptions and entitlements, analytics and crash
        diagnostics, notifications (if used), and support communications. By
        using the App, you acknowledge this Policy and agree to its terms. If
        you do not agree, uninstall the App.
      </p>

      <div className={s.title}>2) Controller and Contact Information</div>
      <p>
        The controller who determines purposes and means of processing is
        Aleksandra Rakitina. For privacy questions or to exercise rights such as
        access, deletion, correction, restriction, objection, or portability,
        contact alexwadek@gmail.com. We verify identity before acting on
        requests, typically by confirming control of the email you use to
        contact us or by requesting limited additional details. We retain
        privacy correspondence for compliance and security documentation as
        permitted by law. Platform billing topics like refunds or cancellations
        are handled in your device account settings; we will still address
        privacy matters within our remit.
      </p>

      <div className={s.title}>
        3) Key Principles (Local-First, Minimization, Transparency)
      </div>
      <p>
        Local-first: scan logs and settings are stored on your device in
        app-scoped storage (Hive) and are not uploaded to our servers.
        Minimization: we collect only what is necessary to deliver features
        (entitlement checks, stability analytics, crash diagnostics) and do not
        sell Personal Data. Transparency: we clearly disclose permission usage
        and third-party providers (subscriptions, analytics/crash). Cloud sync
        is not part of the MVP and is not required to use the App.
      </p>

      <div className={s.title}>4) Definitions</div>
      <p>
        “Personal Data” means information relating to an identified or
        identifiable person. “Scan Data” refers to on-device records of your
        scan sessions (timestamp, scan type such as magnetic or optical, and
        result). “Sensor Data” refers to real-time readings from the
        magnetometer and state of the flashlight used during scans; we do not
        persist raw magnetic waveforms beyond what is needed to render UI and
        store the simple Scan Data result. “Processing” includes collection,
        storage, use, export, and deletion. “Service Providers/Processors” are
        third parties acting on our instructions for subscriptions, analytics,
        crash reporting, and (if enabled) notifications.
      </p>

      <div className={s.title}>5) What We Do Not Collect</div>
      <p>
        We do not operate a backend that receives your Scan Data or settings. We
        do not upload camera frames to our servers. We do not collect payment
        card numbers. We do not require registration for core functionality. We
        do not request precise geolocation or access your contacts. If you
        export or share content via other apps or services (e.g., a PDF log you
        create), those destinations have their own privacy practices we do not
        control.
      </p>

      <div className={s.title}>6) Categories of Data We Process</div>
      <p>
        Depending on your choices, we may process: (a) Scan Data stored locally
        (date/time, scan type, found/not found); (b) Settings stored locally
        (vibration on/off, sound on/off, language, theme); (c)
        Subscription/entitlement metadata from RevenueCat (product identifiers,
        trial status, renewal outcome, expiration/cancellation) to grant premium
        features such as unlimited scans and pro tools; (d) Analytics and crash
        diagnostics via Firebase (app/OS version, device model, anonymized error
        codes, event counters like app_open, scan_started, scan_completed,
        alert_triggered, paywall_shown, purchase_started, purchase_success); (e)
        Notification tokens and preferences if you enable push or local
        reminders. We do not receive payment card details.
      </p>

      <div className={s.title}>7) Sources of Data and Voluntary Provision</div>
      <p>
        Most information originates from you and your device during scans and
        when toggling settings. Technical diagnostics arise automatically to
        help maintain reliability. Certain features require permissions or
        inputs: magnetometer access for magnetic scans; camera/flashlight for
        optical scanning; vibration and sound for alerts; network access for
        subscription validation and analytics SDK updates; notifications if you
        opt in. You can revoke permissions in system settings at any time; doing
        so may limit specific features but not basic access to the App.
      </p>

      <div className={s.title}>8) Magnetometer Use and Thresholds</div>
      <p>
        During magnetic scanning the App reads magnetometer values (measured in
        microteslas, μT) to display a real-time indicator. Thresholds (for
        example, &gt;150 μT “possible device,” &lt;50 μT “clear”) are heuristics
        intended to guide visual feedback; readings are influenced by many
        factors such as speakers, wiring, phone cases, and ambient interference.
        We do not upload raw sensor streams to our servers. The simple result of
        a scan (found/not found) can be saved locally if history is enabled.
      </p>

      <div className={s.title}>9) Camera Scan Mode and Flashlight</div>
      <p>
        In Camera Scan mode, the App uses the camera preview to help you detect
        reflective glints from small lenses. Frames are processed in memory to
        render highlights on your device; we do not upload frames to our
        servers. You may enable the flashlight for dark environments. When the
        App detects a potential reflection, it may trigger haptic feedback and a
        brief on-screen alert. You can disable vibration and sound in Settings.
        We do not store the camera frames; only the high-level scan outcome may
        be written to local history if you keep history enabled.
      </p>

      <div className={s.title}>10) Local Storage, History, and Deletion</div>
      <p>
        Scan history (if enabled) and settings are stored locally in the app
        sandbox (Hive). You may view or delete entries from the History screen.
        Uninstalling the App removes the sandbox and stored data from your
        device. If you manually exported any logs or screenshots using external
        services, those copies remain under the destination’s control and must
        be managed there.
      </p>

      <div className={s.title}>
        11) Free Tier Limits, Paywall, and Entitlements
      </div>
      <p>
        The free tier may allow a limited number of scans (e.g., two) before
        showing a paywall. Paid access unlocks unlimited scans and pro tools.
        Subscriptions are handled by platform billing via RevenueCat and
        currently include $4.99/week with a 3-day free trial (product ID may
        appear as invoice_premium_weekly or equivalent depending on storefront
        configuration). We receive product identifiers, trial eligibility,
        renewal outcomes, expiration dates, and cancellation status to grant or
        revoke access. Manage or cancel subscriptions in device account
        settings; refunds and grace periods follow platform rules. We do not
        receive payment card numbers.
      </p>

      <div className={s.title}>
        12) Analytics, Performance, and Crash Diagnostics
      </div>
      <p>
        We use Firebase Analytics and Crashlytics to understand reliability,
        performance, and feature adoption—not to create behavioral advertising
        profiles. Typical events include app_open, scan_started (type:
        magnetic/camera), scan_completed (found: true/false), alert_triggered
        (type), paywall_shown (source), purchase_started, purchase_success, and
        settings_changed (vibration_on/sound_on). Where feasible, analytics are
        aggregated or pseudonymized and do not include raw camera frames or
        continuous magnetometer streams. System privacy settings may allow you
        to reduce analytics, which can limit troubleshooting of device-specific
        issues.
      </p>

      <div className={s.title}>
        13) Notifications and In-App Messages (Optional)
      </div>
      <p>
        If you enable notifications, the App may send reminders or brief safety
        tips. Delivery requires a device token used only to route messages to
        your device. You can disable notifications in system settings and, where
        available, in-app toggles. We avoid including sensitive content in
        notification bodies. If you uninstall the App, push tokens are typically
        invalidated by the platform, though brief delays may occur before
        complete deactivation.
      </p>

      <div className={s.title}>14) What We Share with Service Providers</div>
      <p>
        To operate the App, we share limited data with providers acting on our
        instructions: RevenueCat for subscriptions/entitlements, Firebase
        Analytics/Crashlytics for measurement and stability, and platform
        notification services if enabled. Providers are bound by terms requiring
        appropriate security and prohibiting the use of Personal Data for their
        independent marketing. We do not upload Scan Data contents (sensor
        streams or camera frames) to these providers.
      </p>

      <div className={s.title}>15) Security Measures</div>
      <p>
        We implement administrative, technical, and organizational safeguards
        appropriate to the risks. Communications with providers occur over
        HTTPS; local data resides in app-scoped storage; sensitive tokens are
        kept using platform mechanisms; we apply least-privilege access to
        operational tools and separate development and production environments.
        While no system is perfectly secure, our local-first architecture
        reduces exposure by keeping scan content on the device unless you export
        something intentionally.
      </p>

      <div className={s.title}>16) Data Retention and Deletion</div>
      <p>
        Local history and settings persist until you delete them or uninstall
        the App. Subscription and entitlement metadata may be retained by
        providers for accounting and fraud prevention as permitted by law,
        typically in pseudonymized or aggregated form. Analytics/crash
        diagnostics are retained for operational trend windows and then deleted
        or de-identified. Verified deletion requests for server-side records we
        control (e.g., support emails) are handled within a reasonable
        timeframe.
      </p>

      <div className={s.title}>17) International Data Transfers</div>
      <p>
        Because Scan Data is local by default, international transfers of scan
        contents do not occur. Limited Personal Data (subscription and analytics
        metadata) may be processed by providers in other jurisdictions. Where
        required, we rely on safeguards such as contractual protections and
        platform mechanisms for cross-border transfers. Providers must implement
        security measures consistent with this Policy and applicable law.
      </p>

      <div className={s.title}>18) Your Rights and Choices</div>
      <p>
        Subject to law, you may have rights to access, correct, delete, or port
        Personal Data; to restrict or object to certain processing; and to
        withdraw consent where processing relies on consent. In-app, you can
        clear history, disable vibration/sound, and change language. Subscription
        management (including cancellation and refunds) occurs in platform
        account settings. To exercise other rights, email alexwadek@gmail.com;
        we will verify identity and respond within a reasonable time.
      </p>

      <div className={s.title}>
        19) Do Not Track, Identifiers, and SDK Disclosures
      </div>
      <p>
        As a native app, we do not use browser cookies. SDKs may store
        identifiers needed for subscriptions, analytics, crash reports, and
        notifications. These identifiers function similarly to cookies by
        enabling sessions, entitlement checks, and measurement. The App does not
        rely on advertising identifiers for targeted ads. Browser “Do Not Track”
        signals generally do not apply to native apps; we keep SDK usage scoped
        to necessary purposes and avoid combining identifiers across services for
        unrelated marketing.
      </p>

      <div className={s.title}>20) Children’s Privacy</div>
      <p>
        The App is not directed to children, and we do not knowingly collect
        Personal Data from users under the age where parental consent is
        required by law. If you believe a minor used the App without appropriate
        authorization, contact alexwadek@gmail.com and we will remove
        information and terminate access where applicable. Device-level controls
        can help limit purchases and notifications.
      </p>

      <div className={s.title}>
        21) Accuracy, Safety, and Limitations
      </div>
      <p>
        The App is a consumer tool intended to assist with basic detection
        workflows. Sensor thresholds and optical reflections can produce false
        positives/negatives and should not be relied on as the sole method of
        inspection. The App does not guarantee detection of all devices and does
        not provide legal, security, or professional advice. Use caution and
        follow local laws. This section concerns product expectations and does
        not limit your statutory privacy rights.
      </p>

      <div className={s.title}>
        22) Third-Party Links and External Destinations
      </div>
      <p>
        If you open links to documentation, support pages, storefronts, or
        platform settings, you leave the App environment and the destination’s
        privacy practices apply. We do not control third-party content and are
        not responsible for their policies. Review permission prompts and terms
        carefully before proceeding.
      </p>

      <div className={s.title}>
        23) Platform Compliance and Review Transparency
      </div>
      <p>
        For clarity to users and reviewers, we disclose: (a) scans and settings
        are stored locally by default; (b) camera frames and sensor streams are
        not uploaded to our servers; (c) subscriptions use platform billing via
        RevenueCat; (d) analytics/crash logs are oriented to stability; and (e) a
        paywall may appear after free-tier limits (e.g., after two scans). Any
        future feature that changes data flows (e.g., optional cloud backup) will
        be disabled by default and accompanied by in-app disclosures and an
        updated Policy before activation.
      </p>

      <div className={s.title}>24) Changes to This Policy</div>
      <p>
        We may update this Policy to reflect operational, legal, or regulatory
        developments. The “Last Updated” date will change when updates occur.
        Material changes that affect your rights or our processing will be
        highlighted in-app, and when required, we will seek consent for new
        uses. Continued use after changes take effect signifies acceptance. If
        you disagree with updates, stop using the App and request deletion of any
        account-linked records we control, subject to verification and applicable
        retention obligations.
      </p>

      <div className={s.title}>
        25) How to Submit Privacy Requests and Verification Steps
      </div>
      <p>
        To exercise your rights, email alexwadek@gmail.com and specify the
        request type (access, deletion, correction, restriction, objection,
        portability). Include helpful context such as device model and app
        version. We verify identity to prevent unauthorized access or deletion
        and may request limited additional details or a confirmation code. Where
        feasible, responses are provided in a portable, machine-readable format.
        We may decline or limit requests when permitted by law (for example, if
        fulfilling a request would compromise security, interfere with legal
        obligations, or infringe the privacy of others).
      </p>
    </PolicyLayout>
  );
}
